import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  deleteRequest,
  getRequest,
  listRequests,
  updateRequest,
  type RequestDetailOut,
  type RequestOut,
} from "@/lib/api/requests";

const ACTIVE_STATUSES = new Set(["pending", "processing"]);

export const REQUESTS_QUERY_KEY = ["requests"] as const;
export const requestKeys = {
  all: REQUESTS_QUERY_KEY,
  detail: (id: string) => ["requests", id] as const,
};

export function useRequests() {
  return useQuery<RequestOut[]>({
    queryKey: REQUESTS_QUERY_KEY,
    queryFn: listRequests,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 5000;
      return data.some((r) => ACTIVE_STATUSES.has(r.status)) ? 3000 : false;
    },
  });
}

export function useRequestDetail(requestId: string | undefined) {
  return useQuery<RequestDetailOut>({
    queryKey: requestKeys.detail(requestId ?? ""),
    queryFn: () => getRequest(requestId as string),
    enabled: Boolean(requestId),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 4000;
      return ACTIVE_STATUSES.has(data.status) ? 3000 : false;
    },
  });
}

export function useUpdateRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, title }: { requestId: string; title: string | null }) =>
      updateRequest(requestId, title),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: REQUESTS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: requestKeys.detail(vars.requestId) });
    },
  });
}

export function useDeleteRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => deleteRequest(requestId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: REQUESTS_QUERY_KEY });
    },
  });
}
